import React, { useEffect, useRef, useState } from "react";
import { Modal, ModalHeader, ModalBody, FormGroup, Label, Input, Button } from "reactstrap";
import SweetAlert from "sweetalert2";
import { toast } from "react-toastify";
import PreLoader from "../../../../share-components/ui/preLoader";
import {
  getProductViewApi,
  uploadHowToStyleVideoApi,
  deleteHowToStyleVideoApi,
} from "../../../../services/manage-product/productService";

// Kept in sync with config('product.how_to_style_video_max_kb') in the Phase 3 backend
// (config/product.php, default 51200 KB = 50MB). The server remains the source of truth
// for this limit — this constant only gives editors immediate feedback before upload.
const MAX_STYLE_VIDEO_BYTES = 50 * 1024 * 1024;
const ALLOWED_VIDEO_TYPES = ["video/mp4", "video/webm"];

// The shared axios interceptor (src/utils/api.jsx) already shows a toast itself for 422
// and 401 responses (and rejects with the raw response body, not an Error, in those two
// cases) — only re-toast here when the rejection is a real AxiosError that wasn't already
// surfaced globally, so mutation failures aren't double-toasted.
const notifyMutationError = (err, fallback) => {
  if (err && err.isAxiosError && err.response?.status !== 404) {
    toast.error(err.response?.data?.message || err.message || fallback);
  }
};

// 422 responses reject with the raw response body itself, not an AxiosError (see the
// interceptor special-casing above) — so `err` here is the standard Laravel
// {message, errors: {field: [...]}} payload directly. Prefers the first field-level
// validation message, falling back to the top-level message.
const extractValidationMessage = (err, fallback) => {
  if (err && typeof err === "object" && !err.isAxiosError) {
    const fieldErrors = err.errors;
    if (fieldErrors && typeof fieldErrors === "object") {
      const firstField = Object.values(fieldErrors)[0];
      if (Array.isArray(firstField) && firstField.length) return firstField[0];
    }
    if (err.message) return err.message;
  }
  return fallback;
};

const validateFile = (file) => {
  if (!ALLOWED_VIDEO_TYPES.includes(file.type)) {
    return "Only MP4 or WebM video files are allowed.";
  }
  if (file.size > MAX_STYLE_VIDEO_BYTES) {
    return `Video exceeds the ${Math.round(MAX_STYLE_VIDEO_BYTES / (1024 * 1024))}MB size limit.`;
  }
  return null;
};

const ManageHowToStyleVideoModal = ({ open, onClose, productId, productCode, productColour }) => {
  const [videoUrl, setVideoUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [busy, setBusy] = useState(false);
  const [fileError, setFileError] = useState("");
  const fileInputRef = useRef(null);

  const fetchVideo = (silent = false) => {
    if (!productId) return;
    if (!silent) {
      setLoading(true);
      setLoadError("");
    }
    getProductViewApi(productId)
      .then(({ response }) => {
        setVideoUrl(response?.product?.how_to_style_video_url || null);
      })
      .catch((err) => {
        // A silent (post-mutation) reconcile failing shouldn't blow away the optimistic
        // state the user already sees — only surface the retry UI for the initial load.
        if (!silent) {
          setLoadError(err?.response?.data?.message || err?.message || "Failed to load the video.");
        }
      })
      .finally(() => {
        if (!silent) setLoading(false);
      });
  };

  useEffect(() => {
    // Fully clear stale state so a later reopen never flashes a previous product's video,
    // whether we're closing or switching to a different productId.
    setVideoUrl(null);
    setLoadError("");
    setLoading(false);
    setBusy(false);
    setFileError("");
    if (fileInputRef.current) fileInputRef.current.value = "";

    if (open && productId) {
      fetchVideo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, productId]);

  const resetFileInput = () => {
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const doUpload = (file) => {
    setFileError("");
    setBusy(true);

    const data = new FormData();
    data.append("video", file);

    uploadHowToStyleVideoApi(productId, data)
      .then(() => {
        toast.success("How to Style video uploaded.");
        // The upload response returns the raw stored path, not a resolved URL — only the
        // Product Details endpoint (getProductViewApi) resolves it, so re-fetch from there.
        fetchVideo(true);
      })
      .catch((err) => {
        setFileError(extractValidationMessage(err, "Failed to upload the video."));
        notifyMutationError(err, "Failed to upload the video.");
      })
      .finally(() => {
        setBusy(false);
        resetFileInput();
      });
  };

  const handleFileSelected = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const validationMessage = validateFile(file);
    if (validationMessage) {
      setFileError(validationMessage);
      resetFileInput();
      return;
    }
    setFileError("");

    if (videoUrl) {
      // Replace: gated behind confirmation since it supersedes the existing video.
      SweetAlert.fire({
        title: "Replace How to Style video?",
        html: "This will permanently remove the current video and upload the new one.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, replace",
        cancelButtonText: "Cancel",
        reverseButtons: true,
      }).then((result) => {
        if (!result.isConfirmed) {
          resetFileInput();
          return;
        }
        doUpload(file);
      });
    } else {
      doUpload(file);
    }
  };

  const handleDelete = () => {
    SweetAlert.fire({
      title: "Delete How to Style video?",
      html: "This will permanently remove the current video.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    }).then((result) => {
      if (!result.isConfirmed) return;

      setBusy(true);
      deleteHowToStyleVideoApi(productId)
        .then(() => {
          setVideoUrl(null);
          toast.success("How to Style video deleted.");
        })
        .catch((err) => notifyMutationError(err, "Failed to delete the video."))
        .finally(() => setBusy(false));
    });
  };

  return (
    <Modal isOpen={open} toggle={onClose} className="modal-dialog-centered" size="lg">
      <ModalHeader toggle={onClose} tag="div">
        <div>How to Style Video</div>
        {(productCode || (Array.isArray(productColour) && productColour.filter(Boolean).length > 0)) && (
          <div style={{ fontSize: "0.8rem", fontWeight: 400, color: "#888", marginTop: 2 }}>
            {[productCode, Array.isArray(productColour) ? productColour.filter(Boolean).join(", ") : productColour]
              .filter(Boolean)
              .join(" · ")}
          </div>
        )}
      </ModalHeader>
      <ModalBody style={{ minHeight: 200, position: "relative" }}>
        {(loading || busy) && <PreLoader withoutText />}

        {!loading && loadError && (
          <div className="alert alert-danger">
            <div className="font-weight-bold mb-1">Video could not be loaded</div>
            <div className="mb-2">{loadError}</div>
            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => fetchVideo()}>
              Retry
            </button>
          </div>
        )}

        {!loading && !loadError && (
          <>
            {videoUrl ? (
              <div className="mb-3">
                <video
                  controls
                  style={{ width: "100%", maxHeight: 360, borderRadius: 4, background: "#000" }}
                  src={videoUrl}
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            ) : (
              <div
                className="d-flex align-items-center justify-content-center text-muted mb-3"
                style={{ height: 160, border: "1px dashed #ccc", borderRadius: 4 }}
              >
                No video uploaded yet.
              </div>
            )}

            <FormGroup className="mb-2">
              {!videoUrl && <Label>Video File (MP4 or WebM, up to 50MB)</Label>}
              <Input
                innerRef={fileInputRef}
                type="file"
                accept="video/mp4,video/webm"
                disabled={busy}
                style={{ display: videoUrl ? "none" : "block" }}
                onChange={handleFileSelected}
              />
            </FormGroup>

            {fileError && <div className="text-danger small mb-2">{fileError}</div>}

            {videoUrl && (
              <div className="d-flex" style={{ gap: 8 }}>
                <Button color="primary" disabled={busy} onClick={() => fileInputRef.current && fileInputRef.current.click()}>
                  Replace
                </Button>
                <Button color="danger" outline disabled={busy} onClick={handleDelete}>
                  Delete
                </Button>
              </div>
            )}
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

export default ManageHowToStyleVideoModal;
